// Copyright (c) Facebook, Inc. and its affiliates.
import React from 'react';
import Grid from '@material-ui/core/Grid';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import Countdown from 'react-countdown-now';
import Link from '@material-ui/core/Link';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { withStyles } from '@material-ui/core';
import LeaderBoard from './LeaderBoard';

import People from './People';
import * as challengePeople from '../challenge_people.json';

import moment from 'moment-timezone';

const Completionist = () => <span>Challenge has completed!</span>

const renderer = ({ days, hours, minutes, seconds, completed }) => {
    if (completed) {
        // Render a completed state
        return <Completionist />;
    } else {
        // Render a countdown
        return <span>{days} days {hours}h {minutes}m {seconds}s</span>;
    }
};

const styles = (theme) => ({
    title: {
        marginTop: '5em'
    },
    overview: {
        marginTop: '2em'
    },
    headings: {
        marginTop: '1em'
    },
    ulItems: {
        paddingLeft: '1.2em'
    },
    preParent: {
        backgroundColor: '#eee',
        border: '1px solid #ddd',
        borderRadius: '2px',
        '& pre': {
            whiteSpace: 'pre-wrap',
        },
        '& span': {
            padding: '1em',
        },
        '& code': {
            fontSize: '12px',
            color: '#000'
        }
    },
    evalAILink: {
        wordBreak: 'break-word'
    },
    teaserImage: {
        width: '100%',
    },
    spanTypography: {
        display: 'inline-block'
    },
    bannerLogo: {
        width: '100%'
    },
    sectionHeader: {
        marginTop: '0.35em'
    },
    evalaiLogo: {
        marginLeft: '-3px'
    }
});

const Challenge = (props) => {
    const deadline = moment.tz("2019-05-27T23:59:59", "Etc/GMT").toDate();
    return (
        <Grid
            container
            justify="center"
            alignItems="center"
        >
            <Grid
                item
                xs={10}
                md={8}
                lg={7}
            >
                <Grid
                    container
                    justify="flex-start"
                    alignItems="flex-start"
                    spacing={16}
                >
                    <Grid item className={props.classes.title} xs={12}>
                        <Typography variant="h3" align="left">
                            LVIS Challenge 2019
                        </Typography>
                    </Grid>


                    <Grid item className={props.classes.overview} xs={12}>
                        <Typography variant="h4" align="left">
                            Overview
                        </Typography>

                        <Typography variant="subtitle1" className={props.classes.headings}  align="left">
                        Today, rigorous evaluation of general purpose object detectors is mostly performed in the few category regime (e.g.
                        80) or when there are a large number of training examples
                        per category (e.g. 100 to 1000+). LVIS provides an opportunity to enable research in the setting where there are a large
                        number of categories and where per-category data is sometimes scarce.
                        </Typography>
                        <Typography variant="subtitle1" className={props.classes.headings} align="left">
                            Given that state-of-the-art deep learning methods for object detection perform poorly
                            in the low-sample regime, we believe that our dataset poses an important and exciting
                            new scientific challenge.
                        </Typography>
                    </Grid>

                    <Grid item className={props.classes.overview} xs={12}>
                        <Typography variant="h5" align="left">
                        Rules and Awards
                        </Typography>

                        <Typography variant="subtitle1" className={props.classes.headings}  align="left">
                        The LVIS Challenge follows the same rules and awards as the
                        <Link href="http://cocodataset.org/#detection-2019"> COCO Challenge</Link>.
                        Please take note of the requirement to submit a technical report describing your entry.
                        Participants must submit a technical report that includes a detailed ablation study of their submission (suggested length 1-4 pages). 
                        The reports will be made public. Please, use this <Link href="http://cocodataset.org/files/tech_report_template.zip"> latex template </Link> 
                        for the report and send it to  <Link href="mailto:coco.iccv19@gmail.com"> coco.iccv19@gmail.com</Link>. 
                        </Typography>
                    </Grid>

                    <Grid item className={props.classes.overview} xs={12}>
                        <Typography variant="h5" align="left">
                        External Data Policy
                        </Typography>

                        <Typography variant="subtitle1" className={props.classes.headings}  align="left">
                        The use of external data is permitted under two conditions:
                        <ol>
                            <li> It does not contain any images in the LVIS v0.5 test set; and </li>
                            <li> The submission technical report must include:
                                <ol type="a">
                                    <li>A description of the external data (including at least: the name of the dataset,
                                        the number of images in it, how the images were collected if it is not a
                                        public dataset, the annotation type such as image-level label or instance-level
                                        boxes or polygons, and a list of the categories that are annotated); and</li>
                                    <li>An ablation study showing the impact of the external data on the LVIS v0.5
                                        validation set while holding all other design factors constant.
                                        Failure to include (a) and (b) will invalidate the submission.</li>
                                </ol>
                            </li>
                        </ol>
                        </Typography>
                    </Grid>

                    <Grid item className={props.classes.overview} xs={12}>
                        <Typography variant="h5" align="left">
                        Evaluation Server
                        </Typography>

                        <Typography variant="subtitle1" className={props.classes.headings}  align="left">
                        The challenge is hosted on <Link href="https://evalai.cloudcv.org/">EvalAI</Link>.
                        The evaluation server is now live at <Link href="https://evalai.cloudcv.org/web/challenges/challenge-page/442/overview">LVIS Challenge 2019</Link>.
                        </Typography>
                    </Grid>

                    <Grid item className={props.classes.overview} xs={12}>
                        <Typography variant="h5" align="left">
                        Baselines
                        </Typography>

                        <Typography variant="subtitle1" className={props.classes.headings}  align="left">
                        The <Link href="https://arxiv.org/abs/1908.03195">LVIS paper on arXiv</Link> now includes two
                        new appendix sections that will be of great interest to anyone who's participating in the challenge.
                        Appendix B shows two Mask R-CNN baselines on the LVIS v0.5 val set and Appendix C is an analysis
                        of how results transfer from the val set to the test set that will be used in the challenge.
                        </Typography>
                    </Grid>

                    <Grid item className={props.classes.overview} xs={12}>
                        <Typography variant="h5" align="left">
                        Challenge Results
                        </Typography>

                        <Typography variant="subtitle1" className={props.classes.headings}  align="left">
                        Leaderboard for the first <Link href="https://evalai.cloudcv.org/web/challenges/challenge-page/442/overview">LVIS Challenge </Link> 
                        at ICCV 2019 is now public. The awards committee selected team strangeturtle and team lvlvisis as co-winners based on their overall 
                        AP and additional considerations including report quality anduse of external data. In addition, team strangeturtle 
                        was awarded the “most innovative” entry award.
                        </Typography>

                        <LeaderBoard year={2019}/>
                    </Grid>

                    {/* <Grid item className={props.classes.headings} xs={12}>
                        <Typography variant="h4" align="left">
                        Dates
                        </Typography>
                    </Grid>
                    <Grid item className={props.classes.versionNumber} xs={12}>
                        <Typography variant="subtitle1" align="left">
                            <Typography component="span" className={props.classes.spanTypography} color="primary">
                                8 Aug 2019
                            </Typography> &mdash; Challenge announced.
                        </Typography>
                        <Typography variant="subtitle1" align="left">
                            <Typography component="span" className={props.classes.spanTypography} color="primary">
                                13 Oct 2019 (23:59:59 GMT)
                            </Typography> &mdash; Submission deadline for participants.
                        </Typography>
                        <Typography variant="subtitle1" align="left">
                            <Typography component="span" className={props.classes.spanTypography} color="primary">
                                27 Oct 2019
                            </Typography> &mdash; Winners announced at the <Link href="http://cocodataset.org/workshop/coco-mapillary-iccv-2019.html"> Joint COCO and Mapillary
                            Recognition Challenge Workshop</Link>.
                        </Typography>
                    </Grid> */}
                </Grid>
            </Grid>
        </Grid>

      )
}

export default withStyles(styles)(Challenge);
